import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { threadsInfiniteList } from "@/app/Threads/GraphQL/threadQueries";
import { ThreadsInfiniteListQueryVariables, ThreadSort } from "@/gql/graphql";
import { ResultOf } from "@graphql-typed-document-node/core";
import { InfiniteData, Query } from "@tanstack/react-query";
import { hasUnread } from "@/app/Threads/utils/hasUnread";

type Result = ResultOf<typeof threadsInfiniteList>;
type Pages = InfiniteData<Result, unknown>;
type PreviousQuery = Query<Pages, Error, Pages, readonly unknown[]> | undefined;
type Options = Parameters<
  typeof useInfiniteGqlQuery<Result, ThreadsInfiniteListQueryVariables>
>[2];

/** getQueryKey puts the query's variables in slot 1. */
function previousStatus(previousQuery: PreviousQuery) {
  const variables = previousQuery?.queryKey[1] as
    | Partial<ThreadsInfiniteListQueryVariables>
    | undefined;

  return variables?.status;
}

/**
 * The defaults a thread list is fetched with. Shared so anything asking about a
 * list off-screen lands on the same cache entry the list itself reads.
 */
export function threadListVariables(
  variables?: Partial<ThreadsInfiniteListQueryVariables>,
): ThreadsInfiniteListQueryVariables {
  return {
    first: 20,
    sort: ThreadSort.LatestMessage,
    ...variables,
    search: variables?.search?.trim() || undefined,
  };
}

/**
 * Shared so anything refetching the list walks the same pages it holds — a
 * refetch without this throws once a second page is cached.
 */
export function threadListNextPageParam(lastPage: Result) {
  const info = lastPage.threads?.paginatorInfo;
  return info?.hasMorePages ? { page: info.currentPage + 1 } : undefined;
}

export function useThreads(
  variables?: Partial<ThreadsInfiniteListQueryVariables>,
  options?: Partial<Options>,
) {
  const result = useInfiniteGqlQuery(
    threadsInfiniteList,
    threadListVariables(variables),
    {
      initialPageParam: { page: 1 },
      getNextPageParam: threadListNextPageParam,
      refetchInterval: 5 * 60_000,
      // The fetcher defaults to keepPreviousData, which is what we want while
      // a search or page loads. Across an inbox/closed change it would show
      // threads the user just filtered out, so drop it for that key change.
      placeholderData: (
        previous: Pages | undefined,
        previousQuery: PreviousQuery,
      ) =>
        previousStatus(previousQuery) === variables?.status
          ? previous
          : undefined,
      ...options,
    },
  );

  const threads =
    result.data?.pages.flatMap((page) => page.threads?.data ?? []) ?? [];

  const unreadCount = threads.filter((t) => hasUnread(t)).length || undefined;

  const { isFetching, fetchNextPage } = result;

  /**
   * Asking for the next page while a refetch is running cancels that refetch —
   * it defaults to cancelRefetch — and the pages it had already replaced stay
   * mixed with the ones it hadn't, under a spinner that never resolves. The
   * list is short whenever a refetch is mid-flight, which is exactly when the
   * end of it scrolls into view.
   */
  function fetchNextPageWhenIdle() {
    if (!isFetching) {
      fetchNextPage();
    }
  }

  return {
    threads,
    unreadCount,
    isLoading: result.isLoading,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: fetchNextPageWhenIdle,
  };
}
